import type { WebcamDrawStroke } from '../components/program/ProgramSlideWebcamDrawOverlay';

export type SlideWebcamDrawExportMode = 'camera' | 'screen' | 'screen_camera';

function sourceSize(source: CanvasImageSource): { w: number; h: number } {
  if (source instanceof HTMLVideoElement) {
    return { w: source.videoWidth, h: source.videoHeight };
  }
  if (source instanceof HTMLImageElement) {
    return { w: source.naturalWidth, h: source.naturalHeight };
  }
  if (source instanceof HTMLCanvasElement) {
    return { w: source.width, h: source.height };
  }
  if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) {
    return { w: source.width, h: source.height };
  }
  return { w: 0, h: 0 };
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  dx: number,
  dy: number,
  dw: number,
  dh: number
) {
  const { w: sw, h: sh } = sourceSize(source);
  if (sw <= 0 || sh <= 0) return;
  const scale = Math.max(dw / sw, dh / sh);
  const rw = sw * scale;
  const rh = sh * scale;
  ctx.drawImage(source, dx + (dw - rw) / 2, dy + (dh - rh) / 2, rw, rh);
}

function drawImageContain(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  dx: number,
  dy: number,
  dw: number,
  dh: number
) {
  const { w: sw, h: sh } = sourceSize(source);
  if (sw <= 0 || sh <= 0) return;
  const scale = Math.min(dw / sw, dh / sh);
  const rw = sw * scale;
  const rh = sh * scale;
  ctx.drawImage(source, dx + (dw - rw) / 2, dy + (dh - rh) / 2, rw, rh);
}

function getPipRect(layerW: number, layerH: number) {
  const pipW = Math.min(layerW * 0.38, 300);
  const pipH = pipW * (9 / 16);
  return {
    x: layerW - 12 - pipW,
    y: layerH - 12 - pipH,
    w: pipW,
    h: pipH,
  };
}

/** 웹캠 화면 확대·상하반전 (CSS transform과 같은 기준: 영역 중심) */
export type WebcamViewTransform = { zoom: number; flipY: boolean; flipX?: boolean };

function applyViewTransform(
  ctx: CanvasRenderingContext2D,
  view: WebcamViewTransform | undefined,
  x: number,
  y: number,
  w: number,
  h: number
) {
  if (!view || (view.zoom === 1 && !view.flipY && !view.flipX)) return;
  const cx = x + w / 2;
  const cy = y + h / 2;
  ctx.translate(cx, cy);
  ctx.scale(view.flipX ? -view.zoom : view.zoom, view.flipY ? -view.zoom : view.zoom);
  ctx.translate(-cx, -cy);
}

function drawPipCover(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  layerW: number,
  layerH: number,
  view?: WebcamViewTransform
) {
  const pip = getPipRect(layerW, layerH);
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(pip.x, pip.y, pip.w, pip.h, 12);
  ctx.clip();
  applyViewTransform(ctx, view, pip.x, pip.y, pip.w, pip.h);
  drawImageCover(ctx, source, pip.x, pip.y, pip.w, pip.h);
  ctx.restore();
}

export function paintWebcamDrawStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: WebcamDrawStroke[],
  w: number,
  h: number
) {
  for (const stroke of strokes) {
    if (stroke.points.length < 2) continue;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    const first = stroke.points[0]!;
    ctx.moveTo(first.x * w, first.y * h);
    for (let i = 1; i < stroke.points.length; i += 1) {
      const p = stroke.points[i]!;
      ctx.lineTo(p.x * w, p.y * h);
    }
    ctx.stroke();
  }
}

export type WebcamDrawExportInput = {
  width: number;
  height: number;
  captureMode: SlideWebcamDrawExportMode;
  screenVideo: HTMLVideoElement | null;
  webcamVideo: HTMLVideoElement | null;
  frozenBlobUrl: string | null;
  strokes: WebcamDrawStroke[];
  view?: WebcamViewTransform;
};

function renderWebcamDrawCompositeSync(
  input: WebcamDrawExportInput,
  frozenImg: HTMLImageElement | null
): HTMLCanvasElement | null {
  const w = Math.max(1, Math.floor(input.width));
  const h = Math.max(1, Math.floor(input.height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  if (input.captureMode === 'screen_camera') {
    const screen = input.screenVideo;
    if (screen && screen.videoWidth > 0) {
      drawImageContain(ctx, screen, 0, 0, w, h);
    }
    if (frozenImg) {
      drawPipCover(ctx, frozenImg, w, h, input.view);
    } else if (input.webcamVideo && input.webcamVideo.videoWidth > 0) {
      drawPipCover(ctx, input.webcamVideo, w, h, input.view);
    }
  } else {
    const source: CanvasImageSource | null =
      frozenImg ?? (input.webcamVideo && input.webcamVideo.videoWidth > 0 ? input.webcamVideo : null);
    if (!source) return null;
    ctx.save();
    applyViewTransform(ctx, input.view, 0, 0, w, h);
    if (input.captureMode === 'screen') {
      drawImageContain(ctx, source, 0, 0, w, h);
    } else {
      drawImageCover(ctx, source, 0, 0, w, h);
    }
    ctx.restore();
  }

  paintWebcamDrawStrokes(ctx, input.strokes, w, h);
  return canvas;
}

function loadFrozenImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('frozen frame load failed'));
    img.src = url;
  });
}

export async function renderWebcamDrawCompositeAsync(input: WebcamDrawExportInput): Promise<HTMLCanvasElement | null> {
  let frozenImg: HTMLImageElement | null = null;
  if (input.frozenBlobUrl) {
    try {
      frozenImg = await loadFrozenImage(input.frozenBlobUrl);
    } catch {
      return null;
    }
  }
  return renderWebcamDrawCompositeSync(input, frozenImg);
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

export function downloadPngBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function defaultWebcamDrawExportFilename() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `slide-webcam-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.png`;
}
